import React, { useMemo, useState, useCallback } from 'react'
import { Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DownloadIcon from '@mui/icons-material/Download'

import { useSnackbar } from 'notistack'
import {
  useGetAllCourierPartnersQuery,
} from '../../../redux/commonApiCalls'
import { postMultipart } from '../../../common/constants'
import { apiConstants } from '../../../common/constants'

import {
  MainContainer,
  AlertContainer,
  RulesListContainer,
  ActionBar,
  ActionBarLeft,
  AddRuleButton,
  DownloadButton,
  ConfirmAllocationButton,
  FocusOverlay,
} from './styled'
import { getBaseRules, DELIVERY_TYPES, DISPATCH_MODES, normalizeAutoAllocation } from './constants'
import StatsSection from './StatsSection'
import RuleCard from './RuleCard'
import AddRuleDialog from './AddRuleDialog'
import DeleteConfirmationDialog from './DeleteConfirmationDialog'
import WeightValidationDialog from './WeightValidationDialog'

/* Courier payment-mode capability — the SAME rule the backend booking gates use
   (ProshipCourierPartner.supportsPaymentMode): a courier can carry COD unless isCod is
   explicitly false, and prepaid unless isPrepaid is explicitly false. Unset = allowed.
   (The old heuristic derived COD as !isPrepaid, which hid every both-mode courier from
   COD rules and ignored isCod entirely.) */
function isCod(courier) {
  return courier.isCod !== false
}

function isPrepaid(courier) {
  return courier.isPrepaid !== false
}

function WeightZoneRulesBuilder({ merchantId, merchantConfig }) {
  const [rules, setRules] = useState(() => getBaseRules())
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [newRuleForm, setNewRuleForm] = useState({
    deliveryType: '',
    dispatchMode: '',
    paymentMode: 'Prepaid',
    qcType: '',
    shipmentType: 'B2C',
  })
  const [copyFromRule, setCopyFromRule] = useState('')
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    ruleId: null,
  })
  const [expandedRule, setExpandedRule] = useState(null)
  const [weightValidationDialog, setWeightValidationDialog] = useState({
    open: false,
    missingWeightRules: [],
    invalidWeightRanges: [],
    invalidInvoiceRanges: [],
    missingFallbackCouriers: [],
    missingZoneRules: [],
    pendingAction: null,
  })

  const [isUploading, setIsUploading] = useState(false)
  const { enqueueSnackbar } = useSnackbar()

  const filter = {
    andfilter: [
      { field: 'is_deleted', operator: 'eq', value: '0' },
      { field: 'isActive', operator: 'ne', value: false },
      { field: 'isExternal', operator: 'eq', value: false },
    ],
    offset: 0,
    limit: 500,
    skipCount: true,
    includes:
      'id,name,cpAccountCode,prozoId,supportedShipmentType,isBlended,type,dispatchMode,isCod,isPrepaid,qc_type',
  }

  const { data: allCouriers } = useGetAllCourierPartnersQuery(filter)
  const b2bCheapestCPList = useMemo(() => {
    return merchantConfig?.b2bCheapestCPList || []
  }, [merchantConfig])

  const courierOptions = useMemo(() => {
    if (!allCouriers) {
      return {
        FORWARD: () => [],
        RVP: () => [],
      }
    }

    const filterForwardCouriers = ({ dispatchMode, shipmentType, paymentMode }) => {
      return allCouriers
        .filter((courier) => {
          // Check dispatch mode match
          if (dispatchMode && courier.dispatchMode !== dispatchMode.toUpperCase()) {
            return false
          }

          // Check shipment type match
          if (shipmentType && courier.supported_shipment !== shipmentType) {
            return false
          }

          // For B2C, check payment mode compatibility
          if (shipmentType === 'B2C') {
            if (paymentMode === 'COD') {
              return isCod(courier)
            }
            if (paymentMode === 'Prepaid') {
              return isPrepaid(courier)
            }
          }

          if (shipmentType === 'B2B') {
            // First check if courier is in B2B cheapest CP list
            if (!b2bCheapestCPList.includes(courier.prozoId)) {
              return false
            }
            // Then check payment mode compatibility
            if (paymentMode === 'COD') {
              return isCod(courier)
            }
            if (paymentMode === 'Prepaid') {
              return isPrepaid(courier)
            }
            return true
          }

          return true
        })
        .filter((courier) => courier.type === 'FORWARD')
        .map((courier) => courier.cpAccountCode)
    }

    const filterRVPCouriers = ({ qcType, shipmentType }) => {
      const rvpCouriers = allCouriers.filter(
        (courier) => courier.type === 'RVP' && courier.supported_shipment === shipmentType
      )

      if (qcType === 'QC') {
        return rvpCouriers
          .filter((courier) => courier.qcType === 'doorstep')
          .map((courier) => courier.cpAccountCode)
      }

      if (qcType === 'Non-QC') {
        return rvpCouriers
          .filter((courier) => !courier.qcType)
          .map((courier) => courier.cpAccountCode)
      }

      return rvpCouriers.map((courier) => courier.cpAccountCode)
    }

    return {
      FORWARD: filterForwardCouriers,
      RVP: filterRVPCouriers,
    }
  }, [allCouriers, b2bCheapestCPList])

  const stats = useMemo(
    () => ({
      total: rules.length,
      forward: rules.filter((r) => r.deliveryType === 'FORWARD').length,
      reverse: rules.filter((r) => r.deliveryType === 'RVP').length,
    }),
    [rules]
  )

  const handleAddRule = useCallback(() => {
    const { deliveryType, dispatchMode, paymentMode, qcType, shipmentType } = newRuleForm

    const sourceRule = copyFromRule
      ? rules.find((rule) => rule.id === parseInt(copyFromRule))
      : null

    const finalDispatchMode = deliveryType === 'FORWARD' && !dispatchMode ? '' : dispatchMode

    // Build rule name
    const deliveryLabel =
      DELIVERY_TYPES.find((type) => type.value === deliveryType)?.label || deliveryType
    const nameParts = [deliveryLabel]
    if (shipmentType) nameParts.push(shipmentType)
    if (deliveryType === 'FORWARD' && paymentMode) {
      nameParts.push(paymentMode)
    } else if (deliveryType === 'RVP' && qcType) {
      nameParts.push(qcType)
    }
    if (finalDispatchMode && finalDispatchMode.toLowerCase() !== 'none') {
      nameParts.push(finalDispatchMode)
    }
    const ruleName = nameParts.join(' / ')

    // Default advanced settings
    const defaultAdvancedSettings = {
      zone: '',
      minWeight: '',
      maxWeight: '',
      minInvoice: '',
      maxInvoice: '',
      pickupPincode: [],
      dropPincode: [],
      channel: [],
      rvpReason: '',
      itemUnits: '',
      uniqueSkuCount: '',
      shipmentType,
    }

    const newRule = {
      id: Date.now(),
      name: ruleName,
      deliveryType,
      shipmentType,
      dispatchMode: finalDispatchMode,
      servicablePickupPincodes: 'ALL',
      servicableDropPincodes: 'ALL',
      ...(deliveryType === 'RVP' ? { qcType: qcType || '' } : { paymentMode }),
      autoAllocation: normalizeAutoAllocation(sourceRule?.autoAllocation),
      fallbackCouriers: [...(sourceRule?.fallbackCouriers || [])],
      courierAccounts: [...(sourceRule?.courierAccounts || [])],
      isDefault: false,
      advancedSettings: sourceRule?.advancedSettings
        ? { ...defaultAdvancedSettings, ...sourceRule.advancedSettings }
        : defaultAdvancedSettings,
      priority: rules.length + 1,
    }

    setRules([...rules, newRule])
    setShowAddDialog(false)
    setNewRuleForm({
      deliveryType: '',
      dispatchMode: '',
      paymentMode: 'Prepaid',
      qcType: '',
      shipmentType: 'B2C',
    })
    setCopyFromRule('')
    enqueueSnackbar('New rule added successfully!', { variant: 'success' })
  }, [newRuleForm, copyFromRule, rules, enqueueSnackbar])

  const handleFormChange = useCallback((field, value) => {
    setNewRuleForm((prev) => {
      const updated = {
        ...prev,
        [field]: value,
      }

      if (field === 'deliveryType' && value === 'RVP') {
        updated.dispatchMode = ''
        updated.paymentMode = '' // Clear paymentMode for RVP
        if (!updated.qcType) {
          updated.qcType = '' // Initialize qcType if not set
        }
      } else if (field === 'deliveryType' && value === 'FORWARD') {
        updated.qcType = '' // Clear qcType for FORWARD
        if (!updated.paymentMode) {
          updated.paymentMode = 'Prepaid' // Set default paymentMode for FORWARD
        }
      }

      return updated
    })
  }, [])

  const handleCopyFromChange = useCallback(
    (ruleId) => {
      setCopyFromRule(ruleId)
      if (ruleId) {
        const sourceRule = rules.find((rule) => rule.id === parseInt(ruleId))

        if (sourceRule) {
          setNewRuleForm({
            deliveryType: sourceRule.deliveryType,
            // Only copy dispatchMode if it exists, clear it for RVP rules
            dispatchMode: sourceRule.deliveryType === 'RVP' ? '' : sourceRule.dispatchMode,
            paymentMode: sourceRule.paymentMode || 'COD',
            qcType: sourceRule.qcType || '',
            shipmentType: sourceRule.shipmentType,
          })
        }
      }
    },
    [rules]
  )

  const handleDeleteRule = useCallback((ruleId) => {
    setDeleteDialog({ open: true, ruleId })
  }, [])

  const confirmDeleteRule = useCallback(() => {
    if (deleteDialog.ruleId) {
      setRules((prev) => prev.filter((rule) => rule.id !== deleteDialog.ruleId))
      setDeleteDialog({ open: false, ruleId: null })
      enqueueSnackbar('Rule deleted successfully!', { variant: 'success' })
    }
  }, [deleteDialog.ruleId, enqueueSnackbar])

  const validateWeights = useCallback(() => {
    const missingWeightRules = rules.filter((rule) => {
      const minWeight = rule.advancedSettings?.minWeight
      const maxWeight = rule.advancedSettings?.maxWeight
      const minWeightStr = String(minWeight || '').trim()
      const maxWeightStr = String(maxWeight || '').trim()
      return !minWeightStr || !maxWeightStr
    })
    return missingWeightRules
  }, [rules])

  const validateWeightRanges = useCallback(() => {
    const invalidWeightRanges = rules.filter((rule) => {
      const minWeight = rule.advancedSettings?.minWeight
      const maxWeight = rule.advancedSettings?.maxWeight
      const minWeightStr = String(minWeight || '').trim()
      const maxWeightStr = String(maxWeight || '').trim()

      // Skip if weights are missing (handled by validateWeights)
      if (!minWeightStr || !maxWeightStr) {
        return false
      }

      // Check if minWeight > maxWeight
      const minWeightNum = parseFloat(minWeightStr)
      const maxWeightNum = parseFloat(maxWeightStr)

      if (isNaN(minWeightNum) || isNaN(maxWeightNum)) {
        return false
      }

      return minWeightNum > maxWeightNum
    })
    return invalidWeightRanges
  }, [rules])

  const validateFallbackCouriers = useCallback(() => {
    return rules.filter((rule) => !rule.fallbackCouriers || rule.fallbackCouriers.length === 0)
  }, [rules])

  const validateZone = useCallback(() => {
    return rules.filter((rule) => !rule.advancedSettings?.zone?.trim())
  }, [rules])

  const validateInvoiceRanges = useCallback(() => {
    const invalidInvoiceRanges = rules.filter((rule) => {
      const minInvoice = rule.advancedSettings?.minInvoice
      const maxInvoice = rule.advancedSettings?.maxInvoice
      const minInvoiceStr = String(minInvoice || '').trim()
      const maxInvoiceStr = String(maxInvoice || '').trim()

      // Allow empty values - if both are empty, that's fine
      if (!minInvoiceStr || !maxInvoiceStr) {
        return false
      }

      // Check if minInvoice > maxInvoice
      const minInvoiceNum = parseFloat(minInvoiceStr)
      const maxInvoiceNum = parseFloat(maxInvoiceStr)

      if (isNaN(minInvoiceNum) || isNaN(maxInvoiceNum)) {
        return false
      }

      return minInvoiceNum > maxInvoiceNum
    })
    return invalidInvoiceRanges
  }, [rules])

  const generateCSVContent = useCallback(() => {
    const maxFallbackCouriers = Math.max(...rules.map((rule) => rule.fallbackCouriers.length))

    const priorityHeaders = []
    for (let i = 2; i <= maxFallbackCouriers + 1; i++) {
      priorityHeaders.push(`Priority ${i}`)
    }

    const csvHeaders = [
      'Delivery Type',
      'Dispatch Mode',
      'Payment Mode',
      'Zone',
      'Min Weight (kg)',
      'Max Weight (kg)',
      'Min Invoice Value(INR)',
      'Max Invoice Value(INR)',
      'Pickup Pincode',
      'Drop Pincode',
      'Channel Name',
      'RVP Reason',
      'Shipment Type',
      'Item Units',
      'Unique SKU Count',
      'Priority 1',
      ...priorityHeaders,
    ]

    const csvData = rules.map((rule) => {
      const priorityData = []
      for (let i = 0; i < maxFallbackCouriers; i++) {
        priorityData.push(rule.fallbackCouriers[i] || '')
      }

      // Helper function to convert array to semicolon-separated string
      const formatPincodeArray = (pincodeArray, isChannel = false) => {
        if (!pincodeArray || pincodeArray.length === 0) {
          return ''
        }
        return Array.isArray(pincodeArray)
          ? isChannel
            ? pincodeArray.map((pincode) => "'" + pincode + "'").join(';')
            : pincodeArray.join(';')
          : pincodeArray
      }

      return [
        rule.deliveryType,
        rule.dispatchMode || '',
        rule.paymentMode || '',
        rule.advancedSettings.zone === 'All' ? '' : (rule.advancedSettings.zone || ''),
        rule.advancedSettings.minWeight || '',
        rule.advancedSettings.maxWeight || '',
        rule.advancedSettings.minInvoice || '',
        rule.advancedSettings.maxInvoice || '',
        rule.servicablePickupPincodes === 'MANUAL' ? formatPincodeArray(rule.advancedSettings.pickupPincode) : '',
        rule.servicableDropPincodes === 'MANUAL' ? formatPincodeArray(rule.advancedSettings.dropPincode) : '',
        formatPincodeArray(rule.advancedSettings.channel, true), // Change this line to use formatPincodeArray
        rule.advancedSettings.rvpReason || '',
        rule.shipmentType || '',
        rule.advancedSettings.itemUnits || '',
        rule.advancedSettings.uniqueSkuCount || '',
        normalizeAutoAllocation(rule.autoAllocation),
        ...priorityData,
      ]
    })

    const csvContent = [
      csvHeaders.join(','),
      ...csvData.map((row) =>
        row.map((field) => `"${String(field).replace(/"/g, '""')}"`).join(',')
      ),
    ].join('\n')

    return csvContent
  }, [rules])

  const performExport = useCallback(() => {
    const csvContent = generateCSVContent()

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute(
      'download',
      `courier-allocation-rules-${new Date().toISOString().split('T')[0]}.csv`
    )
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    enqueueSnackbar('Rules exported successfully!', { variant: 'success' })
  }, [generateCSVContent, enqueueSnackbar])

  const handleExportCSV = useCallback(() => {
    const missingWeightRules = validateWeights()
    const invalidWeightRanges = validateWeightRanges()
    const invalidInvoiceRanges = validateInvoiceRanges()
    const missingFallbackCouriers = validateFallbackCouriers()
    const missingZoneRules = validateZone()

    if (
      missingWeightRules.length > 0 ||
      invalidWeightRanges.length > 0 ||
      invalidInvoiceRanges.length > 0 ||
      missingFallbackCouriers.length > 0 ||
      missingZoneRules.length > 0
    ) {
      setWeightValidationDialog({
        open: true,
        missingWeightRules,
        invalidWeightRanges,
        invalidInvoiceRanges,
        missingFallbackCouriers,
        missingZoneRules,
        pendingAction: 'download',
      })
      return
    }

    performExport()

    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rules, validateWeights, validateWeightRanges, validateInvoiceRanges, validateFallbackCouriers, validateZone, performExport])

  const performUpload = useCallback(async () => {
    if (!merchantId) {
      enqueueSnackbar('Merchant ID is required for upload', {
        variant: 'error',
      })
      return
    }

    setIsUploading(true)
    try {
      const csvContent = generateCSVContent()
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const file = new File(
        [blob],
        `courier-allocation-rules-${new Date().toISOString().split('T')[0]}.csv`,
        { type: 'text/csv' }
      )

      const formData = new FormData()
      formData.append('file', file)
      formData.append('type', 'undefined')
      formData.append('merchantId', merchantId)

      const response = await postMultipart(apiConstants.UPLOAD_MERCHANT_RULE, formData)

      if (response.status === 200) {
        enqueueSnackbar('Rules uploaded successfully!', {
          variant: 'success',
        })
      } else if (response.data === 'Rule Upload Successful.') {
        enqueueSnackbar('Rule Upload Successful', { variant: 'success' })
        window.location.reload()
      } else {
        enqueueSnackbar(response.data || 'Upload failed', {
          variant: 'error',
        })
      }
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Upload failed'
      enqueueSnackbar(message, { variant: 'error' })
    } finally {
      setIsUploading(false)

    }
  }, [merchantId, generateCSVContent, enqueueSnackbar])

  const handleUploadCSV = useCallback(async () => {
    const missingWeightRules = validateWeights()
    const invalidWeightRanges = validateWeightRanges()
    const invalidInvoiceRanges = validateInvoiceRanges()
    const missingFallbackCouriers = validateFallbackCouriers()
    const missingZoneRules = validateZone()

    if (
      missingWeightRules.length > 0 ||
      invalidWeightRanges.length > 0 ||
      invalidInvoiceRanges.length > 0 ||
      missingFallbackCouriers.length > 0 ||
      missingZoneRules.length > 0
    ) {
      setWeightValidationDialog({
        open: true,
        missingWeightRules,
        invalidWeightRanges,
        invalidInvoiceRanges,
        missingFallbackCouriers,
        missingZoneRules,
        pendingAction: 'upload',
      })
      return
    }

    await performUpload()
  }, [validateWeights, validateWeightRanges, validateInvoiceRanges, validateFallbackCouriers, validateZone, performUpload])

  const handleDownloadClick = useCallback(() => {
    handleExportCSV()
  }, [handleExportCSV])

  const handleWeightValidationConfirm = useCallback(async () => {
    const { pendingAction } = weightValidationDialog
    setWeightValidationDialog({
      open: false,
      missingWeightRules: [],
      invalidWeightRanges: [],
      invalidInvoiceRanges: [],
      missingFallbackCouriers: [],
      missingZoneRules: [],
      pendingAction: null,
    })

    if (pendingAction === 'upload') {
      await performUpload()
    } else {
      performExport()
    }
  }, [weightValidationDialog, performExport, performUpload])

  const handleWeightValidationCancel = useCallback(() => {
    setWeightValidationDialog({
      open: false,
      missingWeightRules: [],
      invalidWeightRanges: [],
      invalidInvoiceRanges: [],
      missingFallbackCouriers: [],
      missingZoneRules: [],
      pendingAction: null,
    })
  }, [])

  const handleUpdateRule = useCallback((ruleId, updates) => {
    setRules((prev) =>
      prev.map((rule) => {
        if (rule.id === ruleId) {
          const updatedRule = { ...rule, ...updates }

          // Clear dispatchMode when deliveryType changes to RVP
          if (updates.deliveryType === 'RVP') {
            updatedRule.dispatchMode = ''
            // Remove paymentMode for RVP rules
            if (updatedRule.paymentMode) {
              delete updatedRule.paymentMode
            }
          } else if (updates.deliveryType === 'FORWARD') {
            // Remove qcType for FORWARD rules
            if (updatedRule.qcType) {
              delete updatedRule.qcType
            }
          }

          // Normalize dispatchMode to match DISPATCH_MODES format
          if (updatedRule.dispatchMode) {
            const normalizedMode = DISPATCH_MODES.find(
              (mode) => mode.toLowerCase() === updatedRule.dispatchMode.toLowerCase()
            )
            if (normalizedMode) {
              updatedRule.dispatchMode = normalizedMode
            }
          }

          // Update rule name if relevant fields change
          if (
            updates.dispatchMode !== undefined ||
            updates.paymentMode !== undefined ||
            updates.qcType !== undefined ||
            updates.deliveryType !== undefined ||
            updates.shipmentType !== undefined
          ) {
            const deliveryTypeLabel =
              DELIVERY_TYPES.find((type) => type.value === updatedRule.deliveryType)?.label ||
              updatedRule.deliveryType

            // Build rule name: <Delivery type> / <Shipment type> / <Payment mode or QC type> / <Dispatch mode (if not none)>
            let ruleName = deliveryTypeLabel

            // Add shipment type
            const shipmentType = updatedRule.shipmentType || ''
            const shipmentTypePart = shipmentType ? ` / ${shipmentType}` : ''
            ruleName = `${ruleName}${shipmentTypePart}`

            // Add payment mode (FORWARD) or QC type (RVP)
            if (updatedRule.deliveryType === 'FORWARD') {
              ruleName = `${ruleName} / ${updatedRule.paymentMode || ''}`
            } else if (updatedRule.deliveryType === 'RVP') {
              ruleName = `${ruleName} / ${updatedRule.qcType || ''}`
            }

            // Add dispatch mode only if it's not empty/none
            if (updatedRule.dispatchMode && updatedRule.dispatchMode.toLowerCase() !== 'none') {
              ruleName = `${ruleName} / ${updatedRule.dispatchMode}`
            }

            updatedRule.name = ruleName
          }
          return updatedRule
        }
        return rule
      })
    )
  }, [])

  const handleMoveRule = useCallback((ruleId, direction) => {
    setRules((prev) => {
      const rulesArray = [...prev]
      const currentIndex = rulesArray.findIndex((rule) => rule.id === ruleId)
      const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1

      if (newIndex >= 0 && newIndex < rulesArray.length) {
        ;[rulesArray[currentIndex], rulesArray[newIndex]] = [
          rulesArray[newIndex],
          rulesArray[currentIndex],
        ]
        rulesArray.forEach((rule, index) => {
          rule.priority = index + 1
        })
      }
      return rulesArray
    })
  }, [])

  const handleAccordionChange = useCallback(
    (ruleId) => (event, isExpanded) => {
      setExpandedRule(isExpanded ? ruleId : null)
    },
    []
  )

  const disableUpload =
    !merchantId || isUploading || !merchantConfig?.automatedCourierAllocationEnabled

  return (
    <>
      <ActionBar>
        <ActionBarLeft>
          <AddRuleButton
            variant='outlined'
            startIcon={<AddIcon />}
            onClick={() => setShowAddDialog(true)}
          >
            Add Rule
          </AddRuleButton>

          <DownloadButton
            variant='contained'
            startIcon={<DownloadIcon />}
            color='secondary'
            onClick={handleDownloadClick}
            disabled={isUploading}
          >
            Download Rules
          </DownloadButton>
        </ActionBarLeft>

        <ConfirmAllocationButton
          variant='contained'
          color='error'
          onClick={handleUploadCSV}
          disabled={isUploading || disableUpload}
        >
          Confirm Auto - Allocation
        </ConfirmAllocationButton>
      </ActionBar>

      <AlertContainer severity='warning'>
        <Typography variant='body2'>
          <strong>Important:</strong> Weight fields (Min Weight & Max Weight) and at least 1
          fallback courier partner are mandatory for all rules. Please ensure all rules have weight
          values and at least 1 fallback courier partner before exporting, otherwise you will
          encounter errors during upload.
        </Typography>
      </AlertContainer>

      <MainContainer>
        <StatsSection stats={stats} />

        {expandedRule !== null && <FocusOverlay />}

        <RulesListContainer>
          {rules.length === 0 ? (
            <AlertContainer severity='info'>
              No rules found. Click the Add Rule button to add your first rule.
            </AlertContainer>
          ) : (
            rules.map((rule) => (
              <RuleCard
                key={rule.id}
                rule={rule}
                rules={rules}
                onUpdateRule={handleUpdateRule}
                onDeleteRule={handleDeleteRule}
                onMoveRule={handleMoveRule}
                courierOptions={
                  rule.deliveryType === 'RVP'
                    ? courierOptions.RVP(rule)
                    : courierOptions.FORWARD(rule)
                }
                expandedRule={expandedRule}
                onAccordionChange={handleAccordionChange}
              />
            ))
          )}
        </RulesListContainer>

        <AddRuleDialog
          open={showAddDialog}
          onClose={() => setShowAddDialog(false)}
          newRuleForm={newRuleForm}
          onFormChange={handleFormChange}
          copyFromRule={copyFromRule}
          onCopyFromChange={handleCopyFromChange}
          rules={rules}
          onAddRule={handleAddRule}
        />

        <DeleteConfirmationDialog
          open={deleteDialog.open}
          onClose={() => setDeleteDialog({ open: false, ruleId: null })}
          onConfirm={confirmDeleteRule}
        />

        <WeightValidationDialog
          open={weightValidationDialog.open}
          onClose={handleWeightValidationCancel}
          onConfirm={handleWeightValidationConfirm}
          missingWeightRules={weightValidationDialog.missingWeightRules}
          invalidWeightRanges={weightValidationDialog.invalidWeightRanges}
          invalidInvoiceRanges={weightValidationDialog.invalidInvoiceRanges}
          missingFallbackCouriers={weightValidationDialog.missingFallbackCouriers}
          missingZoneRules={weightValidationDialog.missingZoneRules}
        />
      </MainContainer>
    </>
  )
}

export default WeightZoneRulesBuilder
